import React, { useState } from 'react';
import { Star, MapPin, Phone, Clock, ExternalLink, Utensils, Music, Wine, Users } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { restaurantInfo, menuCategories, galleryImages, testimonials, features } from '../mockData';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const RestaurantPortfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const getIcon = (iconName) => {
    const icons = {
      'Utensils': Utensils,
      'Music': Music,
      'Wine': Wine,
      'Users': Users
    };
    const IconComponent = icons[iconName] || Utensils;
    return <IconComponent size={32} />;
  };

  const filteredGalleryImages = selectedCategory === 'all'
    ? galleryImages
    : galleryImages.filter(img => img.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section id="hero" className="relative pt-20 pb-32 px-4 overflow-hidden" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 z-10">
              <h1 className="text-5xl md:text-7xl font-bold leading-tight" style={{ fontFamily: 'var(--font-serif)', color: 'var(--color-black)' }}>
                {restaurantInfo.name}
              </h1>
              <p className="text-xl md:text-2xl" style={{ color: 'var(--color-black)' }}>
                {restaurantInfo.tagline}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a href={restaurantInfo.swiggyReservation} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" style={{ backgroundColor: 'var(--color-black)', color: 'white' }} className="w-full sm:w-auto">
                    Order on Swiggy
                  </Button>
                </a>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById('menu').scrollIntoView({ behavior: 'smooth' })}
                  className="w-full sm:w-auto"
                >
                  View Menu
                </Button>
              </div>

              {/* Quick Info */}
              <div className="flex flex-wrap gap-6 pt-6 text-sm">
                <div className="flex items-center gap-2">
                  <Clock size={18} style={{ color: 'var(--color-black)' }} />
                  <span>{restaurantInfo.hours}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={18} style={{ color: 'var(--color-black)' }} />
                  <a href={`tel:${restaurantInfo.phone}`} className="hover:underline">{restaurantInfo.phone}</a>
                </div>
              </div>
            </div>

            <div className="relative h-[400px] md:h-[600px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={galleryImages[0].url}
                alt="Restaurant Interior"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="about" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
              Why Choose Aura
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the perfect combination of exceptional cuisine, vibrant atmosphere, and outstanding service
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-cream)' }}>
                    {getIcon(feature.icon)}
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" className="py-20 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
              Our Menu
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover our carefully curated selection of culinary delights
            </p>
          </div>

          <div className="space-y-16">
            {menuCategories.map((category) => (
              <div key={category.id}>
                <div className="mb-8">
                  <h3 className="text-3xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
                    {category.category}
                  </h3>
                  <p className="text-gray-600">{category.description}</p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.items.map((item, index) => (
                    <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                      {item.image && (
                        <div className="h-48 overflow-hidden">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                      )}
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <CardTitle className="text-xl">{item.name}</CardTitle>
                          {item.popular && (
                            <Badge variant="secondary" style={{ backgroundColor: 'var(--color-black)', color: 'white' }}>
                              Popular
                            </Badge>
                          )}
                        </div>
                        <CardDescription>{item.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold" style={{ color: 'var(--color-black)' }}>
                          {item.price}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
              Gallery
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Take a visual tour of our restaurant and cuisine
            </p>

            {/* Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-4">
              {['all', 'food', 'drinks', 'ambiance'].map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  onClick={() => setSelectedCategory(category)}
                  style={selectedCategory === category ? { backgroundColor: 'var(--color-black)', color: 'white' } : {}}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredGalleryImages.map((image) => (
              <div
                key={image.id}
                className="relative aspect-square overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-shadow group cursor-pointer"
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-20 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
              What Our Guests Say
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real experiences from our valued customers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white" style={{ backgroundColor: 'var(--color-black)' }}>
                      {testimonial.avatar}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                      <p className="text-sm text-gray-500">{testimonial.date}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} fill="currentColor" className="text-yellow-500" />
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">{testimonial.review}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
              Visit Us
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're located in the heart of Nagpur at Eternity Mall
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <MapPin size={24} style={{ color: 'var(--color-black)' }} />
                    Address
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">{restaurantInfo.address}</p>
                  <a
                    href={restaurantInfo.googleMapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-4 text-sm hover:underline"
                    style={{ color: 'var(--color-black)' }}
                  >
                    Open in Google Maps <ExternalLink size={16} />
                  </a>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Phone size={24} style={{ color: 'var(--color-black)' }} />
                    Contact
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-gray-700">
                    <strong>Phone:</strong>{' '}
                    <a href={`tel:${restaurantInfo.phone}`} className="hover:underline">
                      {restaurantInfo.phone}
                    </a>
                  </p>
                  <p className="text-gray-700">
                    <strong>Email:</strong>{' '}
                    <a href={`mailto:${restaurantInfo.email}`} className="hover:underline">
                      {restaurantInfo.email}
                    </a>
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Clock size={24} style={{ color: 'var(--color-black)' }} />
                    Hours
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">{restaurantInfo.hours}</p>
                </CardContent>
              </Card>
            </div>

            <div className="h-[600px] rounded-lg overflow-hidden shadow-2xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3721.5!2d79.0882!3d21.1458!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjHCsDA4JzQ1LjAiTiA3OcKwMDUnMTcuNSJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Aura Restro Lounge Location"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RestaurantPortfolio;
